import { dialogClasses } from '@mui/material/Dialog';
import type { Coords, Nullish } from '@witmetrics/utils';
import { getCenter, getDistance } from '@witmetrics/utils/numbers';

// 768px must match Tailwind's md breakpoint (max-md: styles are keyed to it)
export function isMobileViewport() {
  return !window.matchMedia('(min-width: 768px)').matches;
}

export function isInput(e: MouseEvent | KeyboardEvent) {
  return (
    e.target instanceof HTMLInputElement ||
    e.target instanceof HTMLTextAreaElement
  );
}

// eslint-disable-next-line witmetrics/no-multiline-comments
/**
 * Wraps an event handler so it always `preventDefault()`s and `stopPropagation()`s
 * before running. Call with no handler to simply swallow the default (e.g. a
 * drag-over target that only needs to allow a drop).
 */
export function stopDefault<
  E extends { preventDefault: () => void; stopPropagation: () => void },
>(handler?: (e: E) => void) {
  return (e: E) => {
    e.preventDefault();
    e.stopPropagation();
    handler?.(e);
  };
}

export function isDialogsOpen() {
  return document.querySelectorAll(`.${dialogClasses.root}`).length > 0;
}

export const SCALE_CONSTANT = 8; // Arbitrary constant to scale pinch delta

export function getPinchDelta(
  e: TouchEvent,
  touchPoint1: Coords,
  touchPoint2: Coords
) {
  const point1 = buildCoords(e.touches[0]);
  const point2 = buildCoords(e.touches[1]);

  const d1 = getDistance(touchPoint1, touchPoint2);
  const d2 = getDistance(point1, point2);

  const center = getCenter(point1, point2);

  return {
    delta: SCALE_CONSTANT * (d1 - d2),
    center,
    touchPoint1: point1,
    touchPoint2: point2,
  };
}

export function buildCoords(
  e: { clientX: number; clientY: number },
  clientRect?: DOMRect
): Coords {
  const offsetX = clientRect?.left || 0;
  const offsetY = clientRect?.top || 0;
  return [e.clientX - offsetX, e.clientY - offsetY];
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(Error('Unable to convert canvas to blob'));
    }, 'image/png');
  });
}

export function createCanvas(
  width: number,
  height: number,
  willReadFrequently = false
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently });
  if (!ctx) {
    throw new Error('Unable to establish CanvasRenderingContext2D');
  }
  return { canvas, ctx };
}

export async function createImage(src: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = document.createElement('img');
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function scrollIntoView(
  element?: HTMLElement | Nullish,
  options?: ScrollIntoViewOptions
) {
  return element?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    ...options,
  });
}
